import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Alert } from 'react-bootstrap';

import { Modal, onHideHandler, renderShowConfirmationModal } from '../../../../components';
import { Toggler } from '../../../../components/Toggle/Toggle';
import { actions } from '../../../../redux/customerConfigurations';
import { makeFileUpload, makeGetFile } from '../../../../api/ajax';
import { FileUploadDropzone } from '../../../../containers';
import IconPdf from '../../../../assets/img/icn-file.svg';

const MyPrivacyPolicyModal = ({ show, privacyPolicySettings, onModalClose, languages }) => {
  const dispatch = useDispatch();

  const [enabled, setEnabled] = useState(privacyPolicySettings?.enabled ?? false);
  const [selectedLanguage, setSelectedLanguage] = useState(languages?.[0]?.id);
  const [uploadedFile, setUploadedFile] = useState(null);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(null);
  const [showErrorMessage, setShowErrorMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setEnabled(privacyPolicySettings?.enabled ?? false);
  }, [privacyPolicySettings]);

  const handleSave = (enabled, languageId, uploadedFile, deletePrivacyPolicy) => {
    setShowSuccessMessage(null);
    setIsUploading(true);
    makeFileUpload(
      'customerConfigurations/current/privacyPolicy',
      null,
      { enabled, languageId, file: uploadedFile, deletePrivacyPolicy },
      'file',
      'PATCH'
    ).subscribe(
      ({ response }) => {
        dispatch(actions.loadCurrentCustomerConfigurationSuccess(response));
        setShowSuccessMessage('Privacy policy settings have been saved!');
        setShowErrorMessage(null);
        setHasUnsavedChanges(false);
        setSelectedLanguage(languages?.[0]?.id);
        setUploadedFile(null);
        setIsUploading(false);
      },
      ({ response: { error } }) => {
        setHasUnsavedChanges(false);
        setShowErrorMessage(error);
        setIsUploading(false);
      }
    );
  };

  const hideDiscardConfirmation = () => {
    hideModal();
    setShowConfirmModal(false);
  };

  const hideModal = () => {
    setShowSuccessMessage(null);
    setShowErrorMessage(null);
    onModalClose();
  };

  const downloadPolicy = (fileName) => {
    makeGetFile(`customerConfigurations/current/privacyPolicy/${encodeURIComponent(fileName)}`).subscribe(({ response }) => {
      const url = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  };

  const pdfDropHandler = (acceptedFiles) => {
    const uploadedFile = acceptedFiles && acceptedFiles[0];
    setUploadedFile(uploadedFile);
  };

  const languagesIds = languages?.map((l) => l.id) || [];

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, hideDiscardConfirmation)}
      <Modal
        headerText="My privacy policy"
        size="md"
        backdrop="static"
        show={show}
        onHide={() => onHideHandler(hasUnsavedChanges, hideModal, setShowConfirmModal)}
      >
        <Modal.Body>
          <div className="row">
            <div className="col">
              {showSuccessMessage && (
                <Alert variant="success" dismissible onClick={() => setShowSuccessMessage(null)}>
                  {showSuccessMessage}
                </Alert>
              )}
              {showErrorMessage && (
                <Alert variant="danger" dismissible onClick={() => setShowErrorMessage(null)}>
                  {showErrorMessage}
                </Alert>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col d-flex align-items-center">
              <label className="mb-0 mr-2">Enabled</label>
              <Toggler
                className=""
                on={enabled}
                onToggle={() => {
                  setEnabled((prev) => !prev);
                  handleSave(!enabled, selectedLanguage, uploadedFile, false);
                }}
              />
            </div>
          </div>
          {enabled && (
            <>
              <div className="row mt-3">
                <div className="col">
                  <FileUploadDropzone
                    accept="application/pdf"
                    name="privacyPolicy"
                    label={
                      <p>
                        <>Drag and drop or upload your privacy policy file.</>
                      </p>
                    }
                    buttonLabel="Upload file"
                    handleDrop={pdfDropHandler}
                    fileName={uploadedFile && uploadedFile.name}
                    objectUrl={uploadedFile && IconPdf}
                    resetFile={() => setUploadedFile(null)}
                  />
                </div>
              </div>
              {uploadedFile && (
                <div className="row mt-3">
                  <div className="col">
                    <label htmlFor="privacyPolicyLanguages">Select language</label>
                    <select
                      id="privacyPolicyLanguages"
                      className="form-control"
                      value={selectedLanguage}
                      onChange={({ target }) => {
                        setSelectedLanguage(target.value);
                        setHasUnsavedChanges(true);
                      }}
                    >
                      {languages && languages.map(({ id, name }) => (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {
                enabled && (
                  <div className="row mt-4">
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-secondary mr-1"
                        onClick={() => onHideHandler(hasUnsavedChanges, hideModal, setShowConfirmModal)}
                        disabled={isUploading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleSave(enabled, selectedLanguage, uploadedFile, false)}
                        disabled={isUploading}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )
              }
            </>
          )}
          {
            privacyPolicySettings.privacyPolicies.length > 0 && (
              <>
                <hr className="my-4" />
                <div className="row">
                  <div className="col">
                    <h5>Uploaded privacy policies</h5>
                    <ul>
                      {privacyPolicySettings.privacyPolicies
                        .filter(({ languageId }) => languagesIds.includes(languageId))
                        .map(({ fileName, languageId }, index) => (
                          <li key={index} className="d-flex justify-content-between align-items-center mt-2">
                            <div>
                              <button
                                type="button"
                                className="btn btn-link p-0"
                                onClick={() => downloadPolicy(fileName)}
                              >
                                {fileName}
                              </button>
                              <span className="text-muted small ml-2">
                                ({languages?.find(({ id }) => id === languageId)?.name})
                              </span>
                            </div>
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-danger p-0"
                              onClick={() => handleSave(enabled, languageId, null, true)}
                              disabled={isUploading}
                            >
                              Delete
                            </button>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </>
            )
          }
        </Modal.Body>
      </Modal>
    </>
  );
};

MyPrivacyPolicyModal.propTypes = {
  show: PropTypes.bool.isRequired,
  privacyPolicySettings: PropTypes.object,
  onModalClose: PropTypes.func.isRequired,
  languages: PropTypes.array,
};

export default MyPrivacyPolicyModal;
